import { queryOptions } from "@tanstack/react-query";
import { apiFetch } from "@/shared/api";

export interface Agent {
  ref: string;                    // 'system:general' | 'user:<uuid>'
  scope: "system" | "user";
  name: string;                   // slug, unique per (user, scope)
  label: string;
  description: string;
  system_prompt: string;
  skill_ids: string[];            // ['system:docx', ...]
  connector_ids: string[];        // user_connectors UUIDs
  /** Slash-commands the agent surfaces in the composer. Same ref shape as
   *  skills: 'system:<slug>' | 'user:<uuid>'. Empty = no slash menu. */
  command_refs: string[];
  model: string | null;
  is_favorite: boolean;
  /** Cross-scope listings (`scope=all`) tag every user-agent with where
   *  it lives; `null` means personal. System agents have no space. */
  space?: { id: string; name: string } | null;
  /** Raw scope id, returned by every user-agent listing & single-row
   *  GET/PUT. `null` = personal scope; absent on system agents. */
  space_id?: string | null;
}

export interface AgentInput {
  name: string;
  label?: string;
  description?: string;
  system_prompt?: string;
  skill_ids?: string[];
  connector_ids?: string[];
  command_refs?: string[];
  model?: string | null;
  /** Target tenancy. `null` = personal scope; `"<uuid>"` = pinned to that
   *  team space. Sent on every save — backend distinguishes from "field
   *  omitted" by a sentinel, but the new UI is always explicit. */
  space_id?: string | null;
}

export interface AgentsCatalogue {
  agents: Agent[];               // visible in /agents catalogue
  defaultAgent: Agent | null;    // implicit fallback for new sessions
}

type Scope = "personal" | "space" | "all";

/**
 * Process-wide catalogue merged on the backend (system FS + user PG).
 * `staleTime: Infinity` — CRUD mutations invalidate explicitly, nothing
 * else changes the list.
 */
export const agentsCatalogueQueryOptions = (scope: Scope = "space") =>
  queryOptions({
    queryKey: ["agents", scope],
    queryFn: async (): Promise<AgentsCatalogue> => {
      const resp = await apiFetch(
        `/api/agents?scope=${scope}`,
        undefined,
        scope === "all" ? { crossSpace: true } : undefined,
      );
      const data: { agents?: Agent[]; default_agent?: Agent | null } = await resp.json();
      return {
        agents: data.agents ?? [],
        defaultAgent: data.default_agent ?? null,
      };
    },
    staleTime: Infinity,
  });

/** Back-compat: most callers only need the visible catalogue list.
 *
 * `space` (default) — current X-Space-Id; matches old behavior.
 * `all` — every space the caller can see, with a `space` badge per row.
 * `personal` — only personal-scope rows.
 */
export const agentsQueryOptions = (scope: Scope = "space") =>
  queryOptions({
    ...agentsCatalogueQueryOptions(scope),
    select: (c: AgentsCatalogue) => c.agents,
  });

export const defaultAgentQueryOptions = (scope: Scope = "space") =>
  queryOptions({
    ...agentsCatalogueQueryOptions(scope),
    select: (c: AgentsCatalogue) => c.defaultAgent,
  });

export async function createAgent(input: AgentInput): Promise<Agent> {
  const resp = await apiFetch("/api/agents", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!resp.ok) throw new Error(await resp.text());
  return resp.json();
}

export async function updateAgent(uuid: string, input: AgentInput): Promise<Agent> {
  const resp = await apiFetch(`/api/agents/${uuid}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!resp.ok) throw new Error(await resp.text());
  return resp.json();
}

export async function deleteAgent(uuid: string): Promise<void> {
  const resp = await apiFetch(`/api/agents/${uuid}`, { method: "DELETE" });
  if (!resp.ok) throw new Error(await resp.text());
}

export async function duplicateAgent(ref: string): Promise<Agent> {
  const resp = await apiFetch("/api/agents/duplicate", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ref }),
  });
  if (!resp.ok) throw new Error(await resp.text());
  return resp.json();
}

/** UUID from `user:<uuid>` ref, or null for system refs. */
export function agentUuid(agent: Agent): string | null {
  return agent.ref.startsWith("user:") ? agent.ref.slice("user:".length) : null;
}

export async function setAgentFavorite(ref: string, on: boolean): Promise<void> {
  const resp = await apiFetch(`/api/agents/${ref}/favorite`, {
    method: on ? "POST" : "DELETE",
  });
  if (!resp.ok) throw new Error(await resp.text());
}
